import React, { useId, useState } from 'react';

import { HelperText } from '../lib/HelperText';
import { InputAdornment } from '../lib/InputAdornment';
import { TextFieldContainer } from '../lib/TextFieldContainer';
import { SvgIcon } from '../SvgIcon';

import { EyeVisibilityToggle } from './EyeVisibilityToggle';
import { StyledFilledInput, StyledFormControl, StyledLabel } from './styles';
import { TPasswordInputProps } from './types';

export const PasswordField = React.forwardRef<
  HTMLInputElement,
  TPasswordInputProps
>(
  (
    {
      id,
      label,
      error,
      helperText,
      sx,
      classes,
      className,
      fullWidth = true,
      size = 'medium',
      disabled = false,
      value,
      onBlur,
      onFocus,
      ...otherProps
    },
    ref
  ) => {
    const generateId = useId();
    const inputId = id || `text-field-${generateId}`;
    const helperTextId = `helper-text-${generateId}`;

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const togglePasswordVisibility = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsPasswordVisible(!isPasswordVisible);
    };

    const shrink = isFocused || !!value;

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      setIsFocused(false);
    };

    return (
      <TextFieldContainer sx={sx} className={className} fullWidth={fullWidth}>
        <StyledFormControl
          error={error}
          classes={classes}
          variant="filled"
          size={size}
          {...otherProps}
        >
          {label && (
            <StyledLabel
              id={inputId}
              error={error}
              variant="filled"
              shrink={shrink}
            >
              {label}
            </StyledLabel>
          )}
          <StyledFilledInput
            id={inputId}
            size={size}
            type={isPasswordVisible ? 'text' : 'password'}
            disabled={disabled}
            aria-describedby={helperText ? helperTextId : undefined}
            inputRef={ref}
            startAdornment={
              <InputAdornment position="start">
                <SvgIcon name="lock" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <EyeVisibilityToggle
                  visible={isPasswordVisible}
                  onClick={togglePasswordVisibility}
                  disabled={disabled}
                />
              </InputAdornment>
            }
            value={value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...otherProps}
          />
          {helperText ? (
            <HelperText id={helperTextId}>{helperText}</HelperText>
          ) : null}
        </StyledFormControl>
      </TextFieldContainer>
    );
  }
);

PasswordField.displayName = 'PasswordField';
